import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { FiMap } from "react-icons/fi";
import { LuSatellite } from "react-icons/lu";
import CommonTooltip from "./CommonTooltip";
import ModalCloseButton from "./ModalCloseButton";

const locationPin = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -36],
  shadowSize: [41, 41],
});

const LOCATION_ZOOM = 15;

// Parses a latitude/longitude that may arrive as a string; null when it isn't a usable coordinate.
export const toCoordinate = (value, limit) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
};

// True when both coordinates are real; (0, 0) is treated as "no location", same as inventory.
export const hasValidLocation = (latitude, longitude) => {
  const lat = toCoordinate(latitude, 90);
  const lng = toCoordinate(longitude, 180);
  return lat !== null && lng !== null && !(lat === 0 && lng === 0);
};

// The map is mounted inside a modal that has just appeared, so Leaflet has to re-measure it.
const ResizeMapOnOpen = () => {
  const map = useMap();
  useEffect(() => {
    const timer = window.setTimeout(() => map.invalidateSize(), 250);
    return () => window.clearTimeout(timer);
  }, [map]);
  return null;
};

/**
 * Modal showing one pin on a map (default / satellite), the same view the inventory screen opens
 * for a store location. `location` is { latitude, longitude, label }; `t` is the current
 * language's translations.
 */
const LocationMapModal = ({ location, title, onClose, t = {}, isRtl = false }) => {
  const [map, setMap] = useState(null);
  const [mapType, setMapType] = useState("map");
  const [zoom, setZoom] = useState(LOCATION_ZOOM);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!map) return undefined;
    const onZoom = () => setZoom(map.getZoom());
    map.on("zoomend", onZoom);
    return () => map.off("zoomend", onZoom);
  }, [map]);

  const latitude = toCoordinate(location?.latitude, 90);
  const longitude = toCoordinate(location?.longitude, 180);
  const label = location?.label;
  if (latitude === null || longitude === null) return null;
  const position = [latitude, longitude];
  const closeLabel = t.bulkExportClose ?? "Close";

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4" dir={isRtl ? "rtl" : "ltr"}>
      <button type="button" className="absolute inset-0 bg-black/40" onClick={onClose} aria-label={closeLabel} />
      <div
        role="dialog"
        aria-modal="true"
        className="relative flex w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
            <p className="mt-0.5 flex min-w-0 items-center gap-1 text-sm text-slate-500">
              {label && <span className="truncate">{label}</span>}
              <span className="shrink-0 font-mono text-xs" dir="ltr">
                ({latitude}, {longitude})
              </span>
            </p>
          </div>
          <ModalCloseButton onClick={onClose} className="shrink-0" ariaLabel={closeLabel} />
        </div>
        <div className="p-4 sm:p-5">
          <div
            className="relative h-[55dvh] min-h-[320px] max-h-[560px] w-full overflow-hidden rounded-lg border border-slate-200"
            dir="ltr"
          >
            <MapContainer
              ref={setMap}
              center={position}
              zoom={LOCATION_ZOOM}
              zoomControl={false}
              scrollWheelZoom
              className="h-full w-full"
              style={{ zIndex: 0 }}
            >
              {mapType === "map" ? (
                <TileLayer
                  attribution="© OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              ) : (
                <TileLayer
                  attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
                  url="https://mt.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                />
              )}
              <ResizeMapOnOpen />
              <Marker position={position} icon={locationPin}>
                {label && <Popup>{label}</Popup>}
              </Marker>
            </MapContainer>
            <div className="absolute left-2 top-2 z-[1000] flex w-10 flex-col items-center rounded-lg border border-slate-200 bg-white text-slate-800 shadow-md">
              <CommonTooltip text={t.storeZoomIn ?? "Zoom in"} position="right" disableDefaultDescription>
                <button type="button" onClick={() => map?.zoomIn()} aria-label={t.storeZoomIn ?? "Zoom in"} className="flex h-10 w-10 items-center justify-center rounded-t-lg text-lg font-medium hover:bg-slate-100">+</button>
              </CommonTooltip>
              <span className="flex h-8 w-10 items-center justify-center border-y border-slate-200 text-xs font-medium" aria-live="polite">{zoom}x</span>
              <CommonTooltip text={t.storeZoomOut ?? "Zoom out"} position="right" disableDefaultDescription>
                <button type="button" onClick={() => map?.zoomOut()} aria-label={t.storeZoomOut ?? "Zoom out"} className="flex h-10 w-10 items-center justify-center rounded-b-lg text-lg font-medium hover:bg-slate-100">−</button>
              </CommonTooltip>
            </div>
            <div className="absolute right-2 top-2 z-[1000] flex rounded-lg border border-slate-200 bg-white p-1 shadow-md" role="group" aria-label={t.storeMapView ?? "Map view"}>
              <button type="button" onClick={() => setMapType("map")} aria-pressed={mapType === "map"} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${mapType === "map" ? "bg-[#236DB4] text-white" : "text-slate-700 hover:bg-slate-100"}`}><FiMap size={14} aria-hidden="true" />{t.storeDefaultView ?? "Default"}</button>
              <button type="button" onClick={() => setMapType("satellite")} aria-pressed={mapType === "satellite"} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${mapType === "satellite" ? "bg-[#236DB4] text-white" : "text-slate-700 hover:bg-slate-100"}`}><LuSatellite size={15} aria-hidden="true" />{t.storeSatelliteView ?? "Satellite"}</button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LocationMapModal;
